import MdEditor from 'react-markdown-editor-lite'
import 'katex/dist/katex.css'
import 'react-markdown-editor-lite/lib/index.css'
import { MarkdownEditorAttributeProps } from 'Types/Attribute.types'
import { useControl } from 'Contexts/ControlContext'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { handlePasteFunctionality } from 'Utils/common.utils'
import { useMessage } from 'shared/hooks/use-message'
import classes from './MarkdownEditor.module.less'

export const MarkdownEditor = forwardRef<
    { getContainer: () => HTMLTextAreaElement | null },
    MarkdownEditorAttributeProps
>((_props, ref) => {
    const editorRef = useRef<MdEditor>(null)
    const control = useControl()
    const [messageApi] = useMessage()

    const {
        onChange: onChangeValue,
        readonly,
        id,
        addInputRef,
        updateControlValue,
        isPasteFunctionalityEnabled,
        value,
        height,
    } = useMemo(() => {
        return { ..._props, ...(control ?? {}) }
    }, [_props, control])

    useEffect(() => {
        const editor = editorRef.current
        if (editor) {
            const mdEditor = editor.getMdElement()
            mdEditor?.addEventListener('paste', e =>
                handlePasteFunctionality(e, isPasteFunctionalityEnabled, messageApi.error)
            )
            return () => {
                mdEditor?.removeEventListener('paste', e =>
                    handlePasteFunctionality(e, isPasteFunctionalityEnabled, messageApi.error)
                )
            }
        }
    }, [isPasteFunctionalityEnabled, messageApi.error])

    useEffect(() => {
        if (control?.isRegistered) {
            addInputRef?.(editorRef)
            updateControlValue?.(value ?? '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [control?.isRegistered])

    const updateContent = useCallback(
        (content: string) => {
            if (readonly) return
            onChangeValue?.(content)
            updateControlValue?.(content)
        },
        [onChangeValue, readonly, updateControlValue]
    )

    useImperativeHandle(ref, () => ({
        getContainer: () => {
            return editorRef.current?.getMdElement() ?? null
        },
    }))

    return (
        <div className={classes.markdownEditorContainer}>
            <MdEditor
                id={id}
                readOnly={readonly}
                view={{ menu: !readonly, md: true, html: true }}
                value={value}
                style={{ height: height ?? '500px' }}
                renderHTML={text => {
                    try {
                        return convertMarkdownToHTML(text)
                    } catch (error) {
                        console.log('Markdown processing error: ', error)
                        return '<p>Error rendering content</p>'
                    }
                }}
                onChange={({ text }) => updateContent(text)}
                ref={editorRef}
            />
        </div>
    )
})
